import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../services/api';
import { useApi, Spinner, ErrBanner, today, fmtDate } from '../../hooks/useApi';
import './Support.css';

const CATEGORIES = ['General Query','Billing Issue','Technical Problem','Feature Request','Data Issue','Other'];
const PRIORITIES  = ['Low','Medium','High','Critical'];

const STATUS_CLASS = { open:'status-open', in_progress:'status-in_progress', resolved:'status-resolved', closed:'status-closed' };
const PRIORITY_CLASS = { Critical:'priority-critical', High:'priority-high', Medium:'priority-medium', Low:'priority-low' };


export default function Support() {
  const { t: tr } = useTranslation();
  const { user } = useAuth();

  const STATUS_LABEL = { open: tr('support.status.open'), in_progress: tr('support.status.inProgress'), resolved: tr('support.status.resolved'), closed: tr('support.status.closed') };

  const { data: tickets, loading, error, reload } = useApi(() => api.tickets.list(), []);
  const [view, setView]     = useState('list');   // 'list' | 'new' | 'detail'
  const [selected, setSel]  = useState(null);
  const [replyText, setRT]  = useState('');
  const [filter, setFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState('');
  const notify = (m) => { setMsg(m); setTimeout(()=>setMsg(''), 4000); };

  const [newForm, setNF] = useState({
    subject:'', category: CATEGORIES[0], priority:'Medium', description:''
  });

  /* ── Stats ── */
  const all        = tickets || [];
  const statOpen   = all.filter(t=>t.status==='open').length;
  const statIP     = all.filter(t=>t.status==='in_progress').length;
  const statRes    = all.filter(t=>t.status==='resolved').length;
  const statTotal  = all.length;

  /* ── Filtered list ── */
  const filtered = filter==='all' ? all : all.filter(t=>t.status===filter);

  /* ── Open a ticket ── */
  const openTicket = (t) => {
    setSel(t);
    setView('detail');
    setRT('');
  };

  /* ── Submit new ticket ── */
  const submitTicket = async (e) => {
    e.preventDefault();
    if (!newForm.subject.trim() || !newForm.description.trim()) {
      notify('❌ ' + tr('support.errors.subjectDescRequired')); return;
    }
    setSaving(true);
    try {
      await api.tickets.create(newForm);
      notify('✅ ' + tr('support.toasts.ticketSubmitted'));
      setNF({ subject:'', category:CATEGORIES[0], priority:'Medium', description:'' });
      setView('list');
      reload();
    } catch(e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  /* ── Send reply ── */
  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSaving(true);
    try {
      await api.tickets.reply(selected.id, { message: replyText });
      notify('✅ ' + tr('support.toasts.replySent'));
      setRT('');
      // Refresh detail
      const updated = await api.tickets.fetch(selected.id);
      setSel(updated);
      reload();
    } catch(e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  /* ── Close ticket ── */
  const closeTicket = async (id) => {
    if (!window.confirm(tr('support.confirmClose'))) return;
    try {
      await api.tickets.updateStatus(id, 'closed');
      notify('✅ ' + tr('support.toasts.ticketClosed'));
      setView('list');
      reload();
    } catch(e) { notify('❌ ' + e.message); }
  };

  return (
    <div className="support-page">
      {/* Header */}
      <div className="page-header">
        <h2 className="page-title">🎫 {tr('support.title')}</h2>
        <div style={{display:'flex',gap:8}}>
          {view !== 'list' && (
            <button className="btn btn-secondary btn-sm" onClick={()=>setView('list')}>← {tr('support.backToTickets')}</button>
          )}
          {view === 'list' && (
            <button className="btn btn-primary btn-sm" onClick={()=>setView('new')}>＋ {tr('support.raiseNewTicket')}</button>
          )}
        </div>
      </div>

      {msg && <div className="notify-bar">{msg}</div>}

      {/* ── STATS ── */}
      {view === 'list' && (
        <div className="ticket-stats">
          <div className="ticket-stat-card">
            <div className="tsc-icon">🎫</div>
            <div><div className="tsc-label">{tr('support.totalTickets')}</div><div className="tsc-value">{statTotal}</div></div>
          </div>
          <div className="ticket-stat-card">
            <div className="tsc-icon">🟡</div>
            <div><div className="tsc-label">{tr('support.status.open')}</div><div className="tsc-value" style={{color:'#c05621'}}>{statOpen}</div></div>
          </div>
          <div className="ticket-stat-card">
            <div className="tsc-icon">🔵</div>
            <div><div className="tsc-label">{tr('support.status.inProgress')}</div><div className="tsc-value" style={{color:'#1a6fad'}}>{statIP}</div></div>
          </div>
          <div className="ticket-stat-card">
            <div className="tsc-icon">✅</div>
            <div><div className="tsc-label">{tr('support.status.resolved')}</div><div className="tsc-value" style={{color:'#2d9e5f'}}>{statRes}</div></div>
          </div>
        </div>
      )}

      {/* ══════════════════════ LIST VIEW ══════════════════════ */}
      {view === 'list' && (
        <div className="section-card">
          <div className="section-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <span>{tr('support.yourTickets')}</span>
            <div style={{display:'flex',gap:6}}>
              {['all','open','in_progress','resolved','closed'].map(s=>(
                <button key={s}
                  style={{
                    padding:'2px 10px', borderRadius:12, border:'1px solid',
                    fontSize:11, fontWeight:700, cursor:'pointer',
                    background: filter===s ? 'white':'transparent',
                    color: filter===s ? 'var(--primary)':'rgba(255,255,255,0.75)',
                    borderColor: filter===s ? 'white':'rgba(255,255,255,0.35)',
                  }}
                  onClick={()=>setFilter(s)}>
                  {s==='all'?tr('common.all'):STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          {loading && <Spinner />}
          {error   && <ErrBanner msg={error} />}

          {!loading && filtered.length === 0 && (
            <div style={{padding:40,textAlign:'center',color:'var(--text-muted)'}}>
              <div style={{fontSize:40,marginBottom:12}}>🎫</div>
              <div style={{fontWeight:600,marginBottom:6}}>
                {filter==='all' ? tr('support.noTicketsYet') : tr('support.noStatusTickets', { status: STATUS_LABEL[filter]?.toLowerCase() })}
              </div>
              <div style={{fontSize:13}}>
                {filter==='all' ? tr('support.raisePrompt') : tr('support.allClear')}
              </div>
            </div>
          )}

          <div className="ticket-list">
            {filtered.map(t => (
              <div key={t.id} className={`ticket-card ${t.status}`} onClick={()=>openTicket(t)}>
                <div className="ticket-card-top">
                  <div className="ticket-subject">{t.subject}</div>
                  <div className="ticket-meta">
                    <span className={`badge ${PRIORITY_CLASS[t.priority]||'badge-info'}`}>{t.priority}</span>
                    <span className={`badge ${STATUS_CLASS[t.status]}`}>{STATUS_LABEL[t.status]}</span>
                  </div>
                </div>
                <div className="ticket-card-bottom">
                  <span className="ticket-id">#{String(t.id).padStart(5,'0')}</span>
                  <span className="ticket-category">📂 {t.category}</span>
                  <span className="ticket-date">🕐 {fmtDate(t.created_at, true)}</span>
                  {t.reply_count > 0 && (
                    <span style={{fontSize:11.5,color:'var(--primary)'}}>💬 {t.reply_count} {t.reply_count===1?tr('support.reply'):tr('support.replies')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════ NEW TICKET ══════════════════════ */}
      {view === 'new' && (
        <div className="section-card">
          <div className="section-header">{tr('support.raiseNewSupportTicket')}</div>
          <form className="new-ticket-form" onSubmit={submitTicket}>
            <div className="ticket-form-grid">
              <div className="form-group" style={{gridColumn:'1/-1'}}>
                <label>{tr('support.subject')} *</label>
                <input className="form-control" value={newForm.subject} required
                  placeholder={tr('support.subjectPlaceholder')}
                  onChange={e=>setNF({...newForm,subject:e.target.value})}/>
              </div>
              <div className="form-group">
                <label>{tr('support.category')} *</label>
                <select className="form-control" value={newForm.category}
                  onChange={e=>setNF({...newForm,category:e.target.value})}>
                  {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>{tr('support.priority')}</label>
                <select className="form-control" value={newForm.priority}
                  onChange={e=>setNF({...newForm,priority:e.target.value})}>
                  {PRIORITIES.map(p=><option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group ticket-desc">
                <label>{tr('common.description')} *</label>
                <textarea
                  placeholder={tr('support.descriptionPlaceholder')}
                  value={newForm.description}
                  onChange={e=>setNF({...newForm,description:e.target.value})}
                  required />
              </div>
            </div>

            {/* Info box */}
            <div style={{background:'var(--primary-light)',border:'1px solid #b3d4f0',borderRadius:6,padding:'10px 14px',
                         fontSize:12.5,color:'var(--primary-dark)',marginBottom:16,display:'flex',gap:8,alignItems:'flex-start'}}>
              <span style={{fontSize:16,flexShrink:0}}>ℹ️</span>
              <div>
                <strong>{tr('support.supportHours')}</strong> {tr('support.supportHoursValue')}<br/>
                <strong>{tr('support.contactLabel')}</strong> +91 89783 61752 · info@noris.in · <a href="https://noris.in" target="_blank" rel="noreferrer" style={{color:'var(--primary)'}}>noris.in</a>
              </div>
            </div>

            <div className="action-bar" style={{padding:0}}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? '⏳ ' + tr('support.submitting') : '🚀 ' + tr('support.submitTicket')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={()=>setView('list')}>✕ {tr('common.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════════════ DETAIL VIEW ══════════════════════ */}
      {view === 'detail' && selected && (
        <div className="ticket-detail">
          <div className="ticket-detail-header">
            <div className="tdh-title">#{String(selected.id).padStart(5,'0')} — {selected.subject}</div>
            <div className="tdh-meta">
              <span>📂 {selected.category}</span>
              <span><span className={`badge ${PRIORITY_CLASS[selected.priority]}`}>{selected.priority}</span></span>
              <span><span className={`badge ${STATUS_CLASS[selected.status]}`}>{STATUS_LABEL[selected.status]}</span></span>
              <span>🕐 {fmtDate(selected.created_at, true)}</span>
            </div>
          </div>

          <div className="ticket-detail-body">
            {/* Original description */}
            <div style={{fontWeight:700,fontSize:12,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:8}}>
              {tr('support.originalMessage')}
            </div>
            <div className="td-description">{selected.description}</div>

            {/* Reply thread */}
            {(selected.replies || []).length > 0 && (
              <>
                <div style={{fontWeight:700,fontSize:12,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:10}}>
                  {tr('support.conversation')} ({selected.replies.length})
                </div>
                <div className="reply-thread">
                  {(selected.replies||[]).map((r, i) => (
                    <div key={i} className={`reply-bubble ${r.sender_type}`}>
                      <div className={`reply-avatar ${r.sender_type}`}>
                        {r.sender_type==='staff' ? 'N' : (user?.name||'U').charAt(0).toUpperCase()}
                      </div>
                      <div className={`reply-content ${r.sender_type}`}>
                        <div className="reply-text">{r.message}</div>
                        <div className="reply-time">
                          {r.sender_type==='staff' ? '⚡ ' + tr('support.norisSupport') : '👤 ' + tr('support.you')} · {fmtDate(r.created_at, true)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Reply composer (only if not closed) */}
            {selected.status !== 'closed' && selected.status !== 'resolved' ? (
              <div className="reply-composer">
                <div style={{fontWeight:700,fontSize:12,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.4px',marginBottom:8}}>
                  {tr('support.addReply')}
                </div>
                <textarea
                  placeholder={tr('support.replyPlaceholder')}
                  value={replyText} onChange={e=>setRT(e.target.value)} />
                <div className="action-bar" style={{padding:'10px 0 0'}}>
                  <button className="btn btn-primary" onClick={sendReply} disabled={saving||!replyText.trim()}>
                    {saving ? '⏳ ' + tr('support.sending') : '📨 ' + tr('support.sendReply')}
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={()=>closeTicket(selected.id)}>
                    🔒 {tr('support.closeTicket')}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{textAlign:'center',padding:'16px 0',color:'var(--text-muted)',fontSize:13}}>
                <span className={`badge ${STATUS_CLASS[selected.status]}`} style={{fontSize:13,padding:'4px 14px'}}>
                  {STATUS_LABEL[selected.status]}
                </span>
                <div style={{marginTop:8}}>{tr('support.thisTicketIs', { status: selected.status })}</div>
                {selected.status !== 'closed' && (
                  <button className="btn btn-primary btn-sm" style={{marginTop:10}}
                    onClick={async()=>{
                      try {
                        await api.tickets.updateStatus(selected.id,'open');
                        notify('✅ ' + tr('support.toasts.ticketReopened'));
                        const updated = await api.tickets.fetch(selected.id);
                        setSel(updated); reload();
                      } catch(e){notify('❌ '+e.message);}
                    }}>
                    🔄 {tr('support.reopenTicket')}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
